import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, of, switchMap, timer } from 'rxjs';
import { BackendStatusService } from '@app/services/backend-status.service';

/**
 * Feedback non bloccante per l'attività backend:
 * - `showBar`: barra sottile in alto dopo 250ms di attività (anti-flicker).
 * - `showColdStart`: avviso in basso dopo 5s (probabile cold-start Cloud Run).
 * Va montato una sola volta nel root component.
 */
@Component({
  selector: 'app-backend-loading',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (showBar()) {
      <div class="bl-bar" role="progressbar" aria-label="Loading"></div>
    }
    @if (showColdStart()) {
      <div class="bl-cold" role="status" aria-live="polite">
        <span class="bl-cold__spinner" aria-hidden="true"></span>
        <span class="bl-cold__text">Connessione al server…</span>
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }
      .bl-bar {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        z-index: 2000;
        background: rgba(59, 130, 246, 0.15);
        overflow: hidden;
      }
      .bl-bar::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 40%;
        background: #3b82f6;
        animation: bl-slide 1.1s ease-in-out infinite;
      }
      @keyframes bl-slide {
        0% {
          left: -40%;
        }
        100% {
          left: 100%;
        }
      }
      .bl-cold {
        position: fixed;
        right: 16px;
        bottom: 16px;
        z-index: 2000;
        display: flex;
        align-items: center;
        gap: 8px;
        max-width: 92vw;
        padding: 6px 12px;
        background: #ffffff;
        color: #64748b;
        border: 1px solid #e5e7eb;
        border-radius: 999px;
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
        font-size: 13px;
        animation: bl-in 0.2s ease-out;
      }
      @keyframes bl-in {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .bl-cold__text {
        line-height: 1.2;
        white-space: nowrap;
      }
      .bl-cold__spinner {
        width: 14px;
        height: 14px;
        flex: 0 0 auto;
        border-radius: 50%;
        border: 2px solid #dbeafe;
        border-top-color: #3b82f6;
        animation: bl-spin 0.8s linear infinite;
      }
      @keyframes bl-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class BackendLoadingComponent {
  private status = inject(BackendStatusService);

  readonly showBar = toSignal(
    toObservable(this.status.active).pipe(
      switchMap((active) => (active ? timer(250).pipe(map(() => true)) : of(false)))
    ),
    { initialValue: false }
  );

  readonly showColdStart = toSignal(
    toObservable(this.status.active).pipe(
      switchMap((active) => (active ? timer(5000).pipe(map(() => true)) : of(false)))
    ),
    { initialValue: false }
  );
}
